// ============================================================
// InputField — Brand Guide v2
//
// Purple substrate bleed. NO greyscale inputs anywhere.
//
//   rest:  bg surface-2  border-hover  radius 10  color primary
//   focus: bg surface-3  border-focus  glow 12px purple
//
// Wraps a native <input> | <textarea> | <select> via `as` prop
// and exposes every native prop. Optional icon slot.
// ============================================================

import { forwardRef, useState } from 'react'

const InputField = forwardRef(function InputField(
  {
    as = 'input',          // 'input' | 'textarea' | 'select'
    label,
    icon,
    error = false,
    disabled = false,
    style: styleOverride,
    onFocus,
    onBlur,
    children,
    ...rest
  },
  ref,
) {
  const [focused, setFocused] = useState(false)

  const bg = focused
    ? 'rgba(123, 94, 167, 0.07)'
    : 'rgba(123, 94, 167, 0.04)'

  const border = error
    ? 'rgba(232, 93, 58, 0.40)'
    : focused
      ? 'rgba(123, 94, 167, 0.30)'
      : 'rgba(123, 94, 167, 0.12)'

  const glow = focused && !error
    ? '0 0 12px rgba(123, 94, 167, 0.08), inset 0 0 8px rgba(123, 94, 167, 0.03)'
    : error
      ? '0 0 10px rgba(232, 93, 58, 0.10)'
      : 'none'

  const fieldStyle = {
    width: '100%',
    padding: icon ? '12px 14px 12px 40px' : '12px 16px',
    background: disabled ? 'rgba(123, 94, 167, 0.02)' : bg,
    border: `1px solid ${border}`,
    borderRadius: 10,
    color: disabled
      ? 'var(--sc-text-tertiary, #556078)'
      : 'var(--sc-text-primary, #edf2ff)',
    fontSize: 14,
    fontFamily: 'var(--sc-font-ui, var(--font-body))',
    outline: 'none',
    boxShadow: glow,
    transition: 'background 0.25s var(--sc-ease), border-color 0.25s, box-shadow 0.25s',
    cursor: disabled ? 'not-allowed' : 'text',
    appearance: as === 'select' ? 'none' : undefined,
    resize: as === 'textarea' ? 'vertical' : undefined,
    minHeight: as === 'textarea' ? 80 : undefined,
    ...styleOverride,
  }

  const commonProps = {
    ref,
    disabled,
    onFocus: (e) => { setFocused(true); onFocus?.(e) },
    onBlur: (e) => { setFocused(false); onBlur?.(e) },
    style: fieldStyle,
    ...rest,
  }

  const field =
    as === 'textarea' ? (
      <textarea {...commonProps} />
    ) : as === 'select' ? (
      <select {...commonProps}>{children}</select>
    ) : (
      <input {...commonProps} />
    )

  return (
    <label style={{ display: 'block', width: '100%' }}>
      {label && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--sc-text-tertiary, #556078)',
            fontFamily: 'var(--sc-font-mono, var(--font-mono))',
            marginBottom: 6,
          }}
        >
          {label}
        </div>
      )}
      <div style={{ position: 'relative', width: '100%' }}>
        {icon && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 14,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--sc-text-tertiary, #556078)',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              fontSize: 14,
            }}
          >
            {icon}
          </span>
        )}
        {field}
      </div>
    </label>
  )
})

export default InputField
